import React, { useState, useEffect } from 'react';
import {
  Grid,
  makeStyles,
  TextField,
  useTheme,
  useMediaQuery,
  Fade,
  InputLabel,
} from '@material-ui/core';
import FormButton from '../../../shared/FormButton';
import { ApplySchema } from '../../../../utils/form-validations';
import submitForm from '../../../../api/submitForm';
import { func, bool } from 'prop-types';
import styles from './index.styles';
import { Formik, Form, Field } from 'formik';
import {
  preventSubmit,
  scrollToElement,
} from '../../../../utils/functions';
import FormPage from './FormPage';
import Attachment from '../../../svg/Attachment';
import useGetApplyTranslation from '../../../../hooks/useGetApplyTranslation';

const useStyles = makeStyles(styles);

const initialFormValues = {
  name: '',
  email: '',
  phoneNumber: '',
  companyNameOrIdeaName: '',
  ideaDescription: '',
  website: '',
  howDoYouKnowPeopleNeedWhatYouAreMaking: '',
  whatSubstitutesDoPeopleResortTo: '',
  howFarAlongAreYou: '',
  aboutTeam: '',
  howWillYouMakeMoneyAndHowMuchCouldYouMake: '',
  howWillYouGetUsers: '',
  anythingElseWeShouldKnowAboutYourCompany: '',
  file: '',
};

const ApplyForm = ({ setFormIsSent, formIsSent }) => {
  const [
    currentPageNumber,
    setCurrentPageNumber,
  ] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const { form } = useGetApplyTranslation();

  const totalPageNumber = 4;

  const theme = useTheme();
  const classes = useStyles({ formIsSent });

  const smallScreen = useMediaQuery(
    theme.breakpoints.down('xs')
  );
  const mediumScreen = useMediaQuery(
    theme.breakpoints.down('sm')
  );

  useEffect(() => {
    /* A small timeout is needed because otherwise
     * the button will attempt to submit the form because
     * the action of the button will change when we are on
     * the last page */
    setTimeout(() => {
      setIsLastPage(currentPageNumber === totalPageNumber);
    }, 20);
  }, [currentPageNumber]);

  const handlePagination = (newPageNumber) => {
    if (
      newPageNumber <= totalPageNumber &&
      newPageNumber > 0
    ) {
      setCurrentPageNumber(newPageNumber);
      scrollToElement(document.body);
    }
  };

  const handleSubmit = async (
    values,
    { setSubmitting }
  ) => {
    try {
      await submitForm('apply-form', values, !!values.file);
      setFormIsSent(true);
      setSubmitting(false);
      scrollToElement(document.body);
    } catch (error) {
      console.log(error);
      setSubmitting(false);
    }
  };

  if (formIsSent) {
    return null;
  }

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={ApplySchema}
      onSubmit={handleSubmit}
      validateOnBlur={true}
    >
      {({
        dirty,
        isValid,
        isSubmitting,
        setFieldValue,
        errors,
        touched,
      }) => (
        <Form
          className={classes.form}
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          name="apply-form"
          action="/"
        >
          <Grid container direction="column">
            <FormPage isVisible={currentPageNumber === 1}>
              <Field name="name">
                {({ field }) => (
                  <TextField
                    label={form.pageOne[0]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    }`}
                    onKeyPress={(event) =>
                      preventSubmit(event)
                    }
                    error={errors.name && touched.name}
                    {...field}
                  />
                )}
              </Field>
              <Field name="email">
                {({ field }) => (
                  <TextField
                    type="email"
                    label={form.pageOne[1]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    }`}
                    onKeyPress={(event) =>
                      preventSubmit(event)
                    }
                    error={errors.email && touched.email}
                    {...field}
                  />
                )}
              </Field>
              <Field name="phoneNumber">
                {({ field }) => (
                  <TextField
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    label={form.pageOne[2]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    }`}
                    onKeyPress={(event) =>
                      preventSubmit(event)
                    }
                    error={
                      errors.phoneNumber &&
                      touched.phoneNumber
                    }
                    {...field}
                  />
                )}
              </Field>
            </FormPage>
            <FormPage isVisible={currentPageNumber === 2}>
              <Field name="companyNameOrIdeaName">
                {({ field }) => (
                  <TextField
                    label={form.pageTwo[0]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    }`}
                    onKeyPress={(event) =>
                      preventSubmit(event)
                    }
                    error={
                      errors.companyNameOrIdeaName &&
                      touched.companyNameOrIdeaName
                    }
                    {...field}
                  />
                )}
              </Field>
              <Field name="ideaDescription">
                {({ field }) => (
                  <TextField
                    label={form.pageTwo[1]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    } ${classes.textarea} ${
                      smallScreen &&
                      classes.textareaMultilineLabel
                    }`}
                    multiline
                    error={
                      errors.ideaDescription &&
                      touched.ideaDescription
                    }
                    {...field}
                  />
                )}
              </Field>
              <Field name="website">
                {({ field }) => (
                  <TextField
                    label={form.pageTwo[2]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    }`}
                    {...field}
                  />
                )}
              </Field>
              <Field name="file">
                {({ field }) => (
                  <InputLabel
                    className={`${classes.fileInputLabel} ${
                      field.value
                        ? classes.fileInputLabelFilled
                        : classes.fileInputLabelEmpty
                    }`}
                  >
                    {field.value
                      ? field.value.name
                      : form.pageTwo[3]}
                    <Attachment
                      className={classes.attachmentIcon}
                    />
                    <input
                      type="file"
                      name="file"
                      accept=".pdf, .ppt, .pptx, .key"
                      className={classes.fileInput}
                      onChange={(event) => {
                        setFieldValue(
                          'file',
                          event.target.files[0]
                        );
                      }}
                    />
                  </InputLabel>
                )}
              </Field>
            </FormPage>
            <FormPage isVisible={currentPageNumber === 3}>
              <Field name="howDoYouKnowPeopleNeedWhatYouAreMaking">
                {({ field }) => (
                  <TextField
                    label={form.pageThree[0]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    } ${classes.textarea} ${
                      smallScreen &&
                      classes.textareaMultilineLabel
                    }`}
                    multiline
                    error={
                      errors.howDoYouKnowPeopleNeedWhatYouAreMaking &&
                      touched.howDoYouKnowPeopleNeedWhatYouAreMaking
                    }
                    {...field}
                  />
                )}
              </Field>
              <Field name="whatSubstitutesDoPeopleResortTo">
                {({ field }) => (
                  <TextField
                    label={form.pageThree[1]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    } ${classes.textareaLarge}`}
                    multiline
                    error={
                      errors.whatSubstitutesDoPeopleResortTo &&
                      touched.whatSubstitutesDoPeopleResortTo
                    }
                    {...field}
                  />
                )}
              </Field>
              <Field name="howFarAlongAreYou">
                {({ field }) => (
                  <TextField
                    label={form.pageThree[2]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    }  ${classes.textarea}`}
                    multiline
                    error={
                      errors.howFarAlongAreYou &&
                      touched.howFarAlongAreYou
                    }
                    {...field}
                  />
                )}
              </Field>
            </FormPage>
            <FormPage isVisible={currentPageNumber === 4}>
              <Field name="aboutTeam">
                {({ field }) => (
                  <TextField
                    label={form.pageFour[0]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    } ${classes.textarea}`}
                    multiline
                    error={
                      errors.aboutTeam && touched.aboutTeam
                    }
                    {...field}
                  />
                )}
              </Field>
              <Field name="howWillYouMakeMoneyAndHowMuchCouldYouMake">
                {({ field }) => (
                  <TextField
                    label={form.pageFour[1]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    } ${
                      !mediumScreen && classes.textareaLarge
                    }  ${
                      smallScreen &&
                      classes.textareaMultilineLabel
                    }`}
                    multiline
                    error={
                      errors.howWillYouMakeMoneyAndHowMuchCouldYouMake &&
                      touched.howWillYouMakeMoneyAndHowMuchCouldYouMake
                    }
                    {...field}
                  />
                )}
              </Field>
              <Field name="howWillYouGetUsers">
                {({ field }) => (
                  <TextField
                    label={form.pageFour[2]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    } ${classes.textarea}`}
                    multiline
                    error={
                      errors.howWillYouGetUsers &&
                      touched.howWillYouGetUsers
                    }
                    {...field}
                  />
                )}
              </Field>
              <Field name="anythingElseWeShouldKnowAboutYourCompany">
                {({ field }) => (
                  <TextField
                    label={form.pageFour[3]}
                    className={`${classes.formField} ${
                      !field.value
                        ? classes.emptyFormField
                        : ''
                    } ${
                      !mediumScreen && classes.textareaLarge
                    }  ${
                      smallScreen &&
                      classes.textareaMultilineLabel
                    }`}
                    multiline
                    error={
                      errors.anythingElseWeShouldKnowAboutYourCompany &&
                      touched.anythingElseWeShouldKnowAboutYourCompany
                    }
                    {...field}
                  />
                )}
              </Field>
            </FormPage>
            <Grid container item justify="center">
              <Fade
                in={currentPageNumber > 1}
                timeout={{ enter: 300, exit: 0 }}
                unmountOnExit
                mountOnEnter
              >
                <FormButton
                  title={form.buttonTexts[2]}
                  buttonType="button"
                  tailed={!smallScreen}
                  inverted
                  onClick={() =>
                    handlePagination(currentPageNumber - 1)
                  }
                />
              </Fade>
              <FormButton
                title={form.buttonTexts[isLastPage ? 1 : 0]}
                buttonType={
                  isLastPage ? 'submit' : 'button'
                }
                tailed={!smallScreen}
                currentPageNumber={currentPageNumber}
                totalPageNumber={totalPageNumber}
                isDisabled={
                  (isLastPage && (!isValid || !dirty)) ||
                  isSubmitting
                }
                onClick={() =>
                  handlePagination(currentPageNumber + 1)
                }
              />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

ApplyForm.propTypes = {
  setFormIsSent: func,
  formIsSent: bool,
};

export default ApplyForm;
